import React, { useEffect, useState } from 'react'

import { Check, ChevronDown, Search } from 'lucide-react'

import { useDebounce } from '@/core/hooks/utils/useDebounce'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
} from '@/shared/components/atoms/Command'
import { Input } from '@/shared/components/atoms/Input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { cn } from '@/shared/utils/helpers'

/**
 * Verbatim base classes from SelectTrigger. Must stay as a single string literal
 * so tailwind-merge v2 doesn't strip dark: variants (e.g. dark:bg-input) when
 * merging with additional overrides.
 */
const SELECT_TRIGGER_BASE =
	"border-primary/10 dark:border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input dark:hover:bg-input/50 flex w-fit min-w-0 items-center justify-between gap-2 rounded-md border bg-secondary px-3 py-2 text-base md:text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-8 data-[size=sm]:h-9 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"

export interface SearchableSelectorItem {
	id: number | string
	label: string
}

interface SearchableSelectorProps {
	items: SearchableSelectorItem[]
	value: number | string | null
	onChange: (id: number | string | null) => void
	/**
	 * Called with the debounced search query so the parent can drive server-side
	 * filtering. Resetting the query (empty string) is also notified on close.
	 */
	onSearchChange: (query: string) => void
	isLoading?: boolean
	placeholder?: string
	searchPlaceholder?: string
	noResultsText?: string
	/**
	 * When provided, a first "clear" item is shown with this label (e.g. "Any account").
	 * Selecting it calls onChange with null.
	 */
	allOptionLabel?: string
	className?: string
	page?: number
	totalPages?: number
	onPageChange?: (page: number) => void
}

export const SearchableSelector: React.FC<SearchableSelectorProps> = ({
	items,
	value,
	onChange,
	onSearchChange,
	isLoading = false,
	placeholder = 'Select…',
	searchPlaceholder = 'Search…',
	noResultsText = 'No results found',
	allOptionLabel,
	className,
	page = 1,
	totalPages = 1,
	onPageChange,
}) => {
	const [open, setOpen] = useState(false)
	const [searchTerm, setSearchTerm] = useState('')
	const [selectedLabel, setSelectedLabel] = useState<string | null>(null)
	const debouncedSearch = useDebounce(searchTerm, 300)

	useEffect(() => {
		onSearchChange(debouncedSearch)
	}, [debouncedSearch])

	const handleOpenChange = (next: boolean) => {
		setOpen(next)
		if (!next) {
			setSearchTerm('')
			onSearchChange('')
		}
	}

	const handleSelect = (id: number | string | null) => {
		const item = id !== null ? items.find((i) => i.id === id) : null
		setSelectedLabel(item?.label ?? null)
		onChange(id)
		setOpen(false)
		setSearchTerm('')
		onSearchChange('')
	}

	// Keep selectedLabel in sync when value is cleared externally
	useEffect(() => {
		if (value === null) {
			setSelectedLabel(null)
		}
	}, [value])

	const selectedItem = items.find((i) => i.id === value)
	const triggerLabel =
		value !== null
			? (selectedItem?.label ?? selectedLabel ?? String(value))
			: (allOptionLabel ?? placeholder)

	return (
		<Popover open={open} modal onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				{/*
				 * Mirror the exact trigger element that ZonesTab uses:
				 *   <SelectTrigger className="rounded-full h-8 text-sm font-medium">
				 * We use a raw <button> with the same verbatim base classes so that
				 * tailwind-merge v2 doesn't drop dark:bg-input when merging pieces.
				 */}
				<button
					type="button"
					data-slot="select-trigger"
					data-size="sm"
					className={cn(
						SELECT_TRIGGER_BASE,
						'rounded-full h-8 text-sm font-medium cursor-pointer',
						!value && 'text-base font-normal',
						className
					)}
				>
					<span className="max-w-[140px] truncate">{triggerLabel}</span>
					<ChevronDown className="size-4 opacity-50 shrink-0" />
				</button>
			</PopoverTrigger>

			<PopoverContent
				className="w-64 p-0"
				align="start"
				onOpenAutoFocus={(e) => e.preventDefault()}
			>
				<div className="p-2 border-b border-border">
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
						<Input
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							placeholder={searchPlaceholder}
							className="pl-8 h-8 text-sm"
						/>
					</div>
				</div>

				<Command shouldFilter={false}>
					<CommandList>
						<ScrollArea className="h-48">
							{isLoading ? (
								<div className="flex flex-col gap-2 p-2">
									<Skeleton className="h-8 w-full" />
									<Skeleton className="h-8 w-full" />
									<Skeleton className="h-8 w-3/4" />
								</div>
							) : (
								<CommandGroup>
									{allOptionLabel && (
										<CommandItem
											value="__all__"
											onSelect={() => handleSelect(null)}
										>
											<Check
												className={cn(
													'mr-2 size-4 shrink-0',
													value === null ? 'opacity-100' : 'opacity-0'
												)}
											/>
											{allOptionLabel}
										</CommandItem>
									)}

									{items.map((item) => (
										<CommandItem
											key={item.id}
											value={String(item.id)}
											onSelect={() => handleSelect(item.id)}
										>
											<Check
												className={cn(
													'mr-2 size-4 shrink-0',
													value === item.id ? 'opacity-100' : 'opacity-0'
												)}
											/>
											<span className="truncate">{item.label}</span>
										</CommandItem>
									))}

									{items.length === 0 && (
										<CommandEmpty>{noResultsText}</CommandEmpty>
									)}
								</CommandGroup>
							)}
						</ScrollArea>
					</CommandList>
				</Command>

				{totalPages > 1 && onPageChange && (
					<div className="flex items-center justify-between border-t px-3 py-2">
						<button
							type="button"
							disabled={page <= 1}
							onClick={() => onPageChange(Math.max(1, page - 1))}
							className="text-xs text-muted-foreground disabled:opacity-40 hover:text-foreground transition-colors"
						>
							‹ Prev
						</button>
						<span className="text-xs text-muted-foreground">
							{page} / {totalPages}
						</span>
						<button
							type="button"
							disabled={page >= totalPages}
							onClick={() => onPageChange(Math.min(totalPages, page + 1))}
							className="text-xs text-muted-foreground disabled:opacity-40 hover:text-foreground transition-colors"
						>
							Next ›
						</button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	)
}
